import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { QuerySchemaCollection, QuerySchemaWalker } from './query-schema';

export const QueryCapabilityKinds = {
	/** Studio builds the request against a known shape: system collections and Studio-only fields. */
	Ungated: 'ungated',
	/** The collection or the workspace query schema is not loaded; fails closed. */
	Unavailable: 'unavailable',
	/** The query schema is loaded and does not offer this surface. */
	Unsupported: 'unsupported',
	Supported: 'supported',
} as const;

type Kinds = typeof QueryCapabilityKinds;

/** Whether an operation surface can be used in this workspace, and what it accepts when gated. */
export type QueryCapability<T> =
	| { readonly kind: Kinds['Ungated'] | Kinds['Unavailable'] | Kinds['Unsupported'] }
	| { readonly kind: Kinds['Supported']; readonly value: T };

export const UNSUPPORTED_CAPABILITY = { kind: QueryCapabilityKinds.Unsupported } as const satisfies QueryCapability<never>;

/** The gated surface, when the query schema offers it. */
export function supportedSurface<T>(capability: QueryCapability<T>): T | undefined {
	return capability.kind === QueryCapabilityKinds.Supported ? capability.value : undefined;
}

/** Narrows a supported surface to one of its parts; a missing part is unsupported. */
export function mapQueryCapability<T, U extends object>(capability: QueryCapability<T>, pick: (value: T) => U | undefined): QueryCapability<U> {
	if (capability.kind !== QueryCapabilityKinds.Supported) return capability;
	const value = pick(capability.value);
	return value === undefined ? UNSUPPORTED_CAPABILITY : { kind: QueryCapabilityKinds.Supported, value };
}

/** Ungated surfaces allow everything; unavailable and unsupported ones allow nothing. */
export function queryCapabilityAllows<T>(capability: QueryCapability<T>, predicate: (value: T) => boolean): boolean {
	switch (capability.kind) {
		case QueryCapabilityKinds.Ungated:
			return true;
		case QueryCapabilityKinds.Unavailable:
		case QueryCapabilityKinds.Unsupported:
			return false;
		case QueryCapabilityKinds.Supported:
			return predicate(capability.value);
		default:
			capability satisfies never;
			return false;
	}
}

export function isQueryCapabilityAvailable<T>(capability: QueryCapability<T>): boolean {
	return queryCapabilityAllows(capability, () => true);
}

export function resolveCollectionQueryCapability(collection: Pick<ResolvedMonospaceCollection, 'id' | 'isSystem'> | null | undefined, schema: QuerySchemaWalker | null | undefined): QueryCapability<QuerySchemaCollection> {
	if (!collection) return { kind: QueryCapabilityKinds.Unavailable };
	if (collection.isSystem) return { kind: QueryCapabilityKinds.Ungated };
	if (!schema) return { kind: QueryCapabilityKinds.Unavailable };
	const value = schema.collectionById(collection.id);
	return value ? { kind: QueryCapabilityKinds.Supported, value } : UNSUPPORTED_CAPABILITY;
}
